import { useEffect, useState } from "react";
import { StarterKit } from "@tiptap/starter-kit";
import { useEditor as useBaseEditor } from "@tiptap/react";
import type { EditorOptions } from "@tiptap/react";
import { CharacterCount } from "@tiptap/extension-character-count";
import { Collaboration } from "@tiptap/extension-collaboration";
import { Placeholder } from "@tiptap/extension-placeholder";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { Underline } from "@tiptap/extension-underline";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import { TextSelection } from "@tiptap/pm/state";
import { Link } from "@tiptap/extension-link";

import { queryClient } from "@config/queryClient";
import useNoteStore from "@store/useNoteStore";
import { useGetCollaborativeTokens } from "./useGetCollaborativeTokens";
import { useTimeoutRef } from "@hooks/useTimeoutRef";
import { openNoteMentionInNewTab } from "@utils/note.util";
import { cn } from "@utils/tailwind.util";
import { isMac } from "@utils/agent.util";
import { AINote } from "@app-types/notes.type";
import { HOCUS_POCUS_URL } from "@config/constants";
import { COLLABORATIVE_TOKENS_QUERY_KEY, BOUND_NOTE_QUERY_KEY } from "@config/query";
import { CustomKeymap, LinkTooltip, Markdown } from "@components/NoteEditor/extensions";
import { LinkPreview, Document as DocumentComponent } from "@components/NoteEditor/components";
import { Mention, Document } from "@components/NoteEditor/nodes";

const initialExtensions = [
  StarterKit.configure({
    history: false,
    heading: { levels: [1, 2, 3] },
    dropcursor: { width: 4, color: "#406A68" },
  }),
  Underline,
];

const useEditor = () => {
  const { track } = useUserpilot();
  const { activeNoteId } = useNoteStore();
  const { data: collaborativeTokens } = useGetCollaborativeTokens();

  const [hasConnected, setHasConnected] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [hasTrackedTyping, setHasTrackedTyping] = useState(false);
  const { schedule: scheduleConnectionFail } = useTimeoutRef();

  let initialPos: number | null = null;
  let lastDirection: "up" | "down" | null = null;

  const refetchToken = async () => {
    await queryClient.invalidateQueries({
      queryKey: [COLLABORATIVE_TOKENS_QUERY_KEY, "TOKENS"],
      exact: true,
    });
  };

  const [extensions, setExtensions] = useState<EditorOptions["extensions"]>(initialExtensions);

  useEffect(() => {
    // Reset tracking when the note changes
    setHasTrackedTyping(false);
  }, [activeNoteId]);

  useEffect(() => {
    scheduleConnectionFail(() => {
      if (!hasConnected && !isSynced) setHasFailed(true);
    }, 5000);

    if (!activeNoteId || !collaborativeTokens?.access) {
      setExtensions(initialExtensions);
      return;
    }

    const doc = new Y.Doc();

    new IndexeddbPersistence(activeNoteId, doc);

    const provider = new HocuspocusProvider({
      url: HOCUS_POCUS_URL,
      name: activeNoteId,
      token: collaborativeTokens?.access || "",
      document: doc,
      onConnect: () => setHasConnected(true),
      onSynced: () => setIsSynced(true),
      onAuthenticationFailed: ({ reason }) => {
        if (reason === "permission-denied") {
          refetchToken().catch(() => {
            console.error("Authentication failed:", reason);
            setHasFailed(true);
          });
        } else {
          console.error("Authentication failed:", reason);
          setHasFailed(true);
        }
      },
    });

    const extensions = [
      ...initialExtensions,
      Link.configure({
        openOnClick: true,
        protocols: ["http", "https", "mailto"],
        defaultProtocol: "https",
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
          class: "cursor-pointer",
        },
      }),
      LinkTooltip.configure({ TooltipComponent: LinkPreview }),
      Mention.configure({ onClick: (mention) => openNoteMentionInNewTab(mention) }),
      TaskItem.configure({ nested: true }),
      Placeholder.configure({
        placeholder: ({ node }) => (node.content.size > 0 ? "" : "Write your notes here..."),
      }),
      CharacterCount.configure({ mode: "textSize" }),
      Collaboration.configure({ document: doc }),
      Document.configure({ component: DocumentComponent }),
      TaskList,
      Markdown,
      CustomKeymap,
    ];

    setExtensions(extensions);

    return () => {
      provider.destroy();
    };
  }, [activeNoteId, collaborativeTokens?.access]);

  const editor = useBaseEditor(
    {
      extensions,
      editorProps: {
        attributes: {
          class: cn("prose outline-none w-full", "mx-auto leading-5 h-full"),
        },
        handleKeyDown: (view, event) => {
          const { state } = view;
          const { selection } = state;
          const { $from, $to } = selection;

          const startPos = $from.start($from.depth);
          const endPos = $to.end($to.depth);

          const isOptionOrCtrl = isMac() ? event.altKey : event.ctrlKey;

          if (!event.shiftKey || !isOptionOrCtrl) {
            initialPos = null;
            lastDirection = null;
            return false;
          }

          if (initialPos === null) {
            initialPos = selection.from;
          }

          // When ArrowUp is pressed with Shift + Option/Control, extend selection up to the start of the current paragraph.
          // If the last action was moving down, shrink selection to the initial position instead of expanding.
          if (event.key === "ArrowUp" && event.shiftKey && isOptionOrCtrl) {
            const targetPos = startPos;

            if (lastDirection === "down") {
              const tr = state.tr.setSelection(
                TextSelection.create(state.doc, selection.from, initialPos)
              );
              view.dispatch(tr);
              lastDirection = "up";
              return true;
            }

            if (selection.from === targetPos) {
              return true;
            }

            const tr = state.tr.setSelection(
              TextSelection.create(state.doc, targetPos, selection.to)
            );

            view.dispatch(tr);
            lastDirection = "up";
            return true;
          }

          // When ArrowDown is pressed with Shift + Option/Control, extend selection down to the end of the current paragraph.
          // If the last action was moving up, shrink selection to the initial position instead of expanding.
          if (event.key === "ArrowDown" && event.shiftKey && isOptionOrCtrl) {
            const targetPos = Math.max(initialPos, endPos);

            if (lastDirection === "up") {
              const tr = state.tr.setSelection(
                TextSelection.create(state.doc, initialPos, selection.to)
              );
              view.dispatch(tr);
              lastDirection = "down";
              return true;
            }

            if (selection.to === targetPos) {
              return true;
            }

            const tr = state.tr.setSelection(
              TextSelection.create(state.doc, selection.from, targetPos)
            );

            view.dispatch(tr);
            lastDirection = "down";
            return true;
          }

          return false;
        },
      },
      onTransaction: ({ transaction }) => {
        if (transaction.getMeta("local") !== false && transaction.docChanged && !hasTrackedTyping) {
          track("Bound note change");
          setHasTrackedTyping(true);
        }
      },
      onUpdate: ({ editor }) => {
        if (activeNoteId) {
          queryClient.setQueryData([BOUND_NOTE_QUERY_KEY, activeNoteId], (old: AINote) => {
            if (!old) return old;
            const isEmpty = !editor.getText() || editor.getText().trim() === "";
            if (!isEmpty && old.state === "draft") {
              return { ...old, state: "active" };
            }
            return old;
          });
        }
      },
    },
    [extensions]
  );

  useEffect(() => {
    if (editor && editor.isEditable && isSynced) {
      setTimeout(() => {
        editor.commands.focus("end");
      }, 0);
    }
  }, [editor, isSynced]);

  const onExtensionsChange = (options: EditorOptions["extensions"]) => {
    setExtensions(options);
  };

  return {
    editor,
    extensions,
    hasFailed,
    isSynced,
    onExtensionsChange,
  };
};

export { useEditor };
